// Neo's answer charts on the web (TOM-711, cloud ADR 0403): decoding the charts
// an answer showed — `charts` and `answerCharts` together — and the pure pieces
// the renderers draw from: values in the viewer's units, labels in the boat's
// own zone, the usual line and the asked bar, a comparison's pairs and gap, a
// forecast's hours and calmest stretch, an allocation's segments, and a text
// summary of each, which is what a screen reader hears. No DOM, so cloud/web's
// Node vitest covers it. Every value comes from the stored snapshot: nothing
// here fetches, and nothing is filled in where the boat recorded nothing.

import {
  NEO_CHARTS_PER_ANSWER_WITH_VIEWS_MAX,
  NEO_FORECAST_FIELDS,
  durationScale,
  formatDurationSeconds,
  isHourMeter,
  isSecondsUnit,
  neoChartIsBase,
  neoChartTitle,
  parseNeoAnswerChart,
  type NeoAnswerChart,
  type NeoAnswerChartSnapshot,
  type DurationScale,
  type NeoAnswerChartSpec,
  type NeoForecastField,
  type NeoVisualGroup,
  type UnitCategory,
  type UnitSettings,
} from '@neomaris/protocol';

import { categoryFor, formatDisplay, toDisplay } from './displayUnits';

export type BarsSnapshot = Extract<NeoAnswerChartSnapshot, { kind: 'bars' }>;
export type TrendSnapshot = Extract<NeoAnswerChartSnapshot, { kind: 'trend' }>;
export type ForecastSnapshot = Extract<NeoAnswerChartSnapshot, { kind: 'forecast' }>;
export type AllocationSnapshot = Extract<NeoAnswerChartSnapshot, { kind: 'allocation' }>;
export type BarsSpec = Extract<NeoAnswerChartSpec, { kind: 'bars' }>;
export type TrendSpec = Extract<NeoAnswerChartSpec, { kind: 'trend' }>;

// ----- Decoding ------------------------------------------------------------------

/**
 * The charts an answer showed, from a stream event, a history message or a
 * turn read-back: `charts` (what every reader draws) and `answerCharts` (the
 * answer charts, ADR 0403) together, decoded defensively. A record this build
 * cannot draw is dropped, never half-drawn; on a boat page a chart of another
 * boat is dropped too. A repeated id is kept once; at most six.
 */
export function parseNeoChatCharts(
  charts: unknown,
  answerCharts: unknown,
  boatId?: string
): NeoAnswerChart[] {
  const raw = [
    ...(Array.isArray(charts) ? charts : []),
    ...(Array.isArray(answerCharts) ? answerCharts : []),
  ];
  const out: NeoAnswerChart[] = [];
  for (const item of raw) {
    if (out.length >= NEO_CHARTS_PER_ANSWER_WITH_VIEWS_MAX) break;
    const chart = parseNeoAnswerChart(item);
    if (chart === null) continue;
    if (boatId !== undefined && chart.spec.boatId !== boatId) continue;
    if (out.some((c) => c.id === chart.id)) continue;
    out.push(chart);
  }
  return out.sort((a, b) => a.id.localeCompare(b.id));
}

/** True for a chart the original renderer draws (and a board keeps). */
export function isBaseChart(chart: NeoAnswerChart): boolean {
  return neoChartIsBase(chart);
}

// ----- Views ---------------------------------------------------------------------

export interface NeoChartCard {
  groupId: string;
  views: Array<{ recordId: string; label: string; chart: NeoAnswerChart }>;
}

/**
 * The cards of views an answer's record names, keyed by each card's first
 * view (the marker the answer's text keeps). A card with a view this answer
 * does not hold is not a card: each of its charts stands alone.
 */
export function neoChartCards(
  visuals: readonly NeoVisualGroup[] | undefined,
  charts: readonly NeoAnswerChart[]
): Map<string, NeoChartCard> {
  const out = new Map<string, NeoChartCard>();
  for (const group of visuals ?? []) {
    const views = group.views.map((v) => ({
      ...v,
      chart: charts.find((c) => c.id === v.recordId),
    }));
    if (views.length < 2 || views.some((v) => v.chart === undefined)) continue;
    out.set(views[0]!.recordId, {
      groupId: group.id,
      views: views as NeoChartCard['views'],
    });
  }
  return out;
}

// ----- Colour --------------------------------------------------------------------

/** The Marina reading kinds (`--tm-kind-*`). */
export type NeoReadingKind =
  | 'power'
  | 'water'
  | 'fuel'
  | 'engine'
  | 'wind'
  | 'weather'
  | 'depth'
  | 'navigation';

/** The Marina colour a reading takes, from its path. */
export function readingKind(path: string | undefined): NeoReadingKind {
  if (path === undefined) return 'navigation';
  if (path.startsWith('electrical.')) return 'power';
  if (/^tanks\.(freshWater|blackWater|wasteWater|greyWater)/.test(path)) return 'water';
  if (path.startsWith('tanks.fuel') || path.endsWith('.fuel.rate')) return 'fuel';
  if (path.startsWith('propulsion.')) return 'engine';
  if (path.startsWith('environment.wind')) return 'wind';
  if (path.startsWith('environment.depth')) return 'depth';
  if (path.startsWith('environment.')) return 'weather';
  return 'navigation';
}

/** The Marina colour a forecast field takes. */
export function forecastKind(field: NeoForecastField): NeoReadingKind {
  if (field === 'wind' || field === 'gust') return 'wind';
  if (field === 'waves' || field === 'swell' || field === 'wave_period' || field === 'swell_period')
    return 'water';
  return 'weather';
}

/** The CSS colour of a reading kind. */
export function kindColor(kind: NeoReadingKind): string {
  return `var(--tm-kind-${kind})`;
}

// ----- Units ---------------------------------------------------------------------

/** The display category of a forecast field's store unit. */
export function forecastCategory(field: NeoForecastField): UnitCategory | undefined {
  switch (field) {
    case 'wind':
    case 'gust':
      return 'speed';
    case 'temperature':
    case 'feels_like':
      return 'temperature';
    case 'pressure':
      return 'pressure';
    case 'waves':
    case 'swell':
      return 'shortDistance';
    case 'visibility':
      return 'distance';
    default:
      return undefined;
  }
}

/** The category a store unit converts in when no path says more. */
function unitCategory(unit: string | null): UnitCategory | undefined {
  if (unit === 'L') return 'volume';
  return categoryFor(undefined, unit);
}

/**
 * A stored value in the viewer's units. Nautical miles go through metres, the
 * distance category's store unit, as the existing bars do.
 */
export function displayValue(
  value: number,
  unit: string | null,
  category: UnitCategory | undefined,
  settings: UnitSettings,
  /** A duration chart's one unit (TOM-742), from {@link secondsScale}. */
  scale?: DurationScale
): { value: number; unit: string } {
  if (scale !== undefined && isSecondsUnit(unit)) {
    return { value: value / scale.seconds, unit: scale.unit };
  }
  if (unit === 'nm') return toDisplay(value * 1852, 'm', 'distance', settings);
  const shown = toDisplay(value, unit, category, settings);
  return { value: shown.value, unit: shown.unit === '' ? (unit ?? '') : shown.unit };
}

/**
 * The one unit a chart of durations draws in (TOM-742): for a series in
 * seconds, the largest unit its biggest value reaches, never more than 60 s;
 * undefined for any other unit.
 */
export function secondsScale(
  unit: string | null,
  values: readonly (number | null | undefined)[],
  /** The series' path: an engine's hour meter draws in hours. */
  path?: string
): DurationScale | undefined {
  if (!isSecondsUnit(unit)) return undefined;
  if (isHourMeter(path, unit)) return { unit: 'h', seconds: 3600 };
  let max = 0;
  for (const v of values)
    if (typeof v === 'number' && Number.isFinite(v)) max = Math.max(max, Math.abs(v));
  return durationScale(max);
}

/** A value as text in the viewer's units, one decimal at most for small numbers. */
export function displayText(
  value: number,
  unit: string | null,
  category: UnitCategory | undefined,
  settings: UnitSettings
): string {
  if (unit === 'nm' || category === undefined) {
    const d = displayValue(value, unit, category, settings);
    return `${roundShown(d.value)}${d.unit === '' ? '' : ` ${d.unit}`}`;
  }
  const f = formatDisplay(value, unit, category, settings);
  return `${f.text}${f.unit === '' ? '' : ` ${f.unit}`}`;
}

/** A number as the owner reads it: whole above 100, one decimal below. */
export function roundShown(v: number): string {
  const a = Math.abs(v);
  if (a >= 100) return String(Math.round(v));
  if (a >= 10) return (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
  return (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
}

// ----- Time labels in the boat's zone ------------------------------------------

/** The zone to format in: UTC for null (the server's "no zone"), the viewer's own for undefined. */
function zoneOrUtc(zone: string | null | undefined): string | undefined {
  if (zone === undefined) return undefined;
  if (zone === null) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return zone;
  } catch {
    return 'UTC';
  }
}

/** "09:00" in the zone. */
export function clockLabel(iso: string | number, zone: string | null | undefined): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: zoneOrUtc(zone),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

/** "Sat 10 Oct" in the zone. */
export function dayLabel(iso: string | number, zone: string | null | undefined): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: zoneOrUtc(zone),
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));
}

/** "PDT" for an instant in the zone. */
function zoneAbbrev(iso: string, zone: string | null): string {
  const part = new Intl.DateTimeFormat('en-US', {
    timeZone: zoneOrUtc(zone),
    timeZoneName: 'short',
  })
    .formatToParts(new Date(iso))
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? '';
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function keyDate(key: string): Date {
  return new Date(`${key.slice(0, 10)}T00:00:00Z`);
}

// ----- Bars ----------------------------------------------------------------------

export interface BarView {
  key: string;
  /** Under the bar: "Sat", "19", "1 PDT" for a doubled hour, "21 Sep". */
  label: string;
  /** For the summary and the tooltip: "Sat 10 Oct", "19:00", "01:00 PST". */
  longLabel: string;
  value: number | null;
  text: string;
  partial: boolean;
  asked: boolean;
}

export interface BarsView {
  unit: string;
  bars: BarView[];
  /** The usual line in display units, or null. */
  usual: { value: number; text: string; periods: number } | null;
  /** The scale's top and bottom, in display units: the bottom below zero only for a negative bar. */
  max: number;
  min: number;
  kind: NeoReadingKind;
  summary: string;
}

const AGGREGATE_WORDS: Record<string, string> = {
  sum: 'Total',
  mean: 'Average',
  max: 'Highest',
  min: 'Lowest',
  delta: 'Change',
};

/** The bars as drawn: labels in the boat's zone, values in the viewer's units. */
export function barsView(spec: BarsSpec, snapshot: BarsSnapshot, settings: UnitSettings): BarsView {
  const zone = snapshot.timezone;
  const category =
    snapshot.unit === 'L'
      ? 'volume'
      : spec.metric === 'reading' && snapshot.aggregate !== 'sum'
        ? categoryFor(spec.path, snapshot.unit)
        : unitCategory(snapshot.unit);
  // Durations draw in one unit across the chart, and each bar's own text
  // reads as a duration does everywhere (TOM-742).
  const scale = secondsScale(
    snapshot.unit,
    [...snapshot.bars.map((b) => b.value), snapshot.usual?.value],
    spec.metric === 'reading' ? spec.path : undefined
  );
  const absolute = (v: number) => displayValue(v, snapshot.unit, category, settings, scale);
  const barText = (raw: number, shown: number) =>
    scale === undefined || scale.unit === 'h' ? roundShown(shown) : formatDurationSeconds(raw);
  // A change converts by scale only: 5 °C warmer is 9 °F warmer, not 41 °F.
  const conv =
    snapshot.aggregate === 'delta'
      ? (v: number) => ({ value: absolute(v).value - absolute(0).value, unit: absolute(0).unit })
      : absolute;
  const sample = absolute(0);
  // One local hour shown twice (a fall-back night): same date and hour, two offsets.
  const hourOffsets = new Map<string, Set<string>>();
  if (spec.by === 'hour') {
    for (const b of snapshot.bars) {
      const at = b.key.slice(0, 13);
      const set = hourOffsets.get(at) ?? new Set<string>();
      set.add(b.key.slice(16));
      hourOffsets.set(at, set);
    }
  }
  const bars = snapshot.bars.map((b, i): BarView => {
    let label: string;
    let longLabel: string;
    if (spec.by === 'hour') {
      const h = b.key.slice(11, 13);
      const date = keyDate(b.key);
      const weekday = WEEKDAYS[date.getUTCDay()]!;
      const doubled = (hourOffsets.get(b.key.slice(0, 13))?.size ?? 0) > 1;
      const abbrev = doubled ? ` ${zoneAbbrev(b.start, zone)}` : '';
      // The first hour of a new local day says which day it is.
      const wraps = i > 0 && snapshot.bars[i - 1]!.key.slice(0, 10) !== b.key.slice(0, 10);
      label = wraps ? weekday : `${Number(h)}${abbrev}`;
      longLabel = `${weekday} ${h}:00${abbrev}`;
    } else if (spec.by === 'day') {
      const d = keyDate(b.key);
      label = WEEKDAYS[d.getUTCDay()]!;
      longLabel = `${label} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
    } else {
      const d = keyDate(b.key);
      label = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
      longLabel = `week of ${label}`;
    }
    const value = b.value === null ? null : conv(b.value).value;
    return {
      key: b.key,
      label,
      longLabel,
      value,
      text: value === null || b.value === null ? '—' : barText(b.value, value),
      partial: b.partial,
      asked: snapshot.highlight === b.key,
    };
  });
  const usual =
    snapshot.usual === undefined || snapshot.usual === null
      ? null
      : {
          value: conv(snapshot.usual.value).value,
          text: barText(snapshot.usual.value, conv(snapshot.usual.value).value),
          periods: snapshot.usual.periods,
        };
  const values = bars.map((b) => b.value).filter((v): v is number => v !== null);
  const max = Math.max(0, ...values, usual?.value ?? 0);
  const min = Math.min(0, ...values, usual?.value ?? 0);
  const unit = sample.unit;
  const asked = bars.find((b) => b.asked);
  // The protocol's title reads a missing aggregate as the mean; so does this.
  const what =
    spec.metric === 'usage'
      ? `Use per ${spec.by}`
      : spec.metric === 'reading'
        ? `${AGGREGATE_WORDS[snapshot.aggregate ?? spec.aggregate ?? 'mean']} per ${spec.by}`
        : `${spec.metric === 'engine_hours' ? 'Engine hours' : 'Distance'} per ${spec.by}`;
  const parts = [
    `${what}, ${unit}.`,
    bars
      .map(
        (b) =>
          `${b.longLabel}: ${b.value === null ? 'no data' : `${b.text}${b.partial ? ' (partial)' : ''}`}`
      )
      .join('; ') + '.',
  ];
  if (usual !== null)
    parts.push(`Usual ${usual.text} ${unit}, from ${usual.periods} earlier ${spec.by}s.`);
  if (asked !== undefined) parts.push(`Asked: ${asked.longLabel}.`);
  return {
    unit,
    bars,
    usual,
    max: max === 0 && min === 0 ? 1 : max,
    min,
    kind: readingKind(spec.path ?? (spec.metric === 'engine_hours' ? 'propulsion.' : undefined)),
    summary: parts.join(' '),
  };
}

// ----- A comparison ---------------------------------------------------------------

export interface ComparePoint {
  /** Milliseconds on the primary series' clock: the slot's middle. */
  t: number;
  v: number | null;
}

export interface CompareView {
  unit: string;
  series: Array<{
    label: string;
    role: 'primary' | 'compare';
    points: ComparePoint[];
    kind: NeoReadingKind;
  }>;
  /** Pairs where both series have a value, for the shaded gap. */
  pairs: Array<{ t: number; a: number; b: number }>;
  from: number;
  to: number;
  min: number;
  max: number;
  zone: string | null | undefined;
  shade: boolean;
  summary: string;
}

/**
 * Two series on the primary's clock. An "earlier" series is moved forward by
 * its shift. A slot with no data stays a gap; the shaded gap covers only the
 * slots where both have data.
 */
export function compareView(
  spec: TrendSpec,
  snapshot: TrendSnapshot,
  settings: UnitSettings,
  zone: string | null | undefined = undefined
): CompareView {
  // Durations draw in one unit across the chart, from every series in
  // seconds (TOM-742).
  const timed = snapshot.series.filter((s) => isSecondsUnit(s.unit));
  const scale = secondsScale(
    timed[0]?.unit ?? null,
    timed.flatMap((s) => [...s.slots.mean, ...(s.slots.min ?? []), ...(s.slots.max ?? [])]),
    timed[0]?.path
  );
  const series = snapshot.series.map((s, i) => {
    const category = categoryFor(s.path, s.unit);
    const shiftMs = (s.shiftSeconds ?? 0) * 1000;
    const start = Date.parse(s.slots.start) + shiftMs;
    const step = s.slots.stepSeconds * 1000;
    return {
      label:
        s.shiftSeconds !== undefined
          ? `${s.label ?? s.path}, ${shiftWords(s.shiftSeconds)} earlier`
          : (s.label ?? s.path),
      role: (s.role ?? (i === 0 ? 'primary' : 'compare')) as 'primary' | 'compare',
      points: s.slots.mean.map((v, j) => ({
        t: start + (j + 0.5) * step,
        v: v === null ? null : displayValue(v, s.unit, category, settings, scale).value,
      })),
      kind: readingKind(s.path),
      unit: displayValue(0, s.unit, category, settings, scale).unit,
    };
  });
  const [a, b] = series;
  const pairs: CompareView['pairs'] = [];
  if (a !== undefined && b !== undefined) {
    a.points.forEach((p, j) => {
      const q = b.points[j];
      if (p.v !== null && q !== undefined && q.v !== null) pairs.push({ t: p.t, a: p.v, b: q.v });
    });
  }
  const values = series
    .flatMap((s) => s.points.map((p) => p.v))
    .filter((v): v is number => v !== null);
  const gap = snapshot.gap;
  const unit = series[0]?.unit ?? '';
  const summary = [
    `${series.map((s) => s.label).join(' and ')}, ${unit}.`,
    ...snapshot.series.map(
      (s, i) =>
        `${series[i]!.label}: ${s.stats === null ? 'no data' : `${roundShown(displayValue(s.stats.min, s.unit, categoryFor(s.path, s.unit), settings, scale).value)} to ${roundShown(displayValue(s.stats.max, s.unit, categoryFor(s.path, s.unit), settings, scale).value)}`}.`
    ),
    gap === undefined || gap === null || gap.first === null || gap.last === null
      ? ''
      : `The gap went from ${roundShown(deltaDisplay(gap.first, snapshot.series[0]!, settings, scale))} to ${roundShown(deltaDisplay(gap.last, snapshot.series[0]!, settings, scale))} ${unit}, over ${gap.paired} paired times.`,
  ].filter((p) => p.length > 0);
  return {
    unit,
    series: series.map(({ unit: _u, ...s }) => s),
    pairs,
    from: Date.parse(spec.window.from),
    to: Date.parse(spec.window.to),
    min: values.length === 0 ? 0 : Math.min(...values),
    max: values.length === 0 ? 1 : Math.max(...values),
    zone,
    shade: snapshot.shade === true || spec.shade === true,
    summary: summary.join(' '),
  };
}

/** A difference in display units: a temperature gap converts by scale only. */
function deltaDisplay(
  delta: number,
  series: { path: string; unit: string | null },
  settings: UnitSettings,
  scale?: DurationScale
): number {
  const category = categoryFor(series.path, series.unit);
  return (
    displayValue(delta, series.unit, category, settings, scale).value -
    displayValue(0, series.unit, category, settings, scale).value
  );
}

function shiftWords(seconds: number): string {
  const hours = seconds / 3600;
  if (hours % 168 === 0) return hours === 168 ? 'a week' : `${hours / 168} weeks`;
  if (hours % 24 === 0) return hours === 24 ? 'a day' : `${hours / 24} days`;
  return `${hours} hours`;
}

// ----- A forecast -----------------------------------------------------------------

export interface ForecastView {
  unit: string;
  series: Array<{
    field: NeoForecastField;
    label: string;
    points: ComparePoint[];
    kind: NeoReadingKind;
    dashed: boolean;
  }>;
  from: number;
  to: number;
  min: number;
  max: number;
  calmest: { from: number; to: number; text: string } | null;
  /** Fields the cached forecast does not carry, said plainly. */
  missing: string | null;
  /** "Open-Meteo · fetched 14:05" in the boat's zone. */
  provenance: string;
  zone: string | null;
  summary: string;
}

export function forecastView(snapshot: ForecastSnapshot, settings: UnitSettings): ForecastView {
  const zone = snapshot.timezone;
  const series = snapshot.series.map((s, i) => {
    const category = forecastCategory(s.field);
    const start = Date.parse(s.points.start);
    const step = s.points.stepSeconds * 1000;
    return {
      field: s.field,
      label: s.label,
      kind: forecastKind(s.field),
      // A second field on the same chart (gusts beside wind) is dashed.
      dashed: i > 0,
      unit: displayValue(0, s.unit, category, settings).unit,
      points: s.points.values.map((v, j) => ({
        t: start + j * step,
        v: v === null ? null : displayValue(v, s.unit, category, settings).value,
      })),
    };
  });
  const values = series
    .flatMap((s) => s.points.map((p) => p.v))
    .filter((v): v is number => v !== null);
  const first = series[0]?.points ?? [];
  const from = first[0]?.t ?? Date.parse(snapshot.fetchedAt);
  const to = first[first.length - 1]?.t ?? from;
  const unit = series[0]?.unit ?? '';
  const h = snapshot.highlight;
  const calmest =
    h === null
      ? null
      : {
          from: Date.parse(h.from),
          to: Date.parse(h.to),
          text: `Calmest ${clockLabel(h.from, zone)}–${clockLabel(h.to, zone)}: ${h.values
            .map((v) => {
              const category = forecastCategory(v.field);
              const lo = displayValue(v.min, v.unit, category, settings);
              const hi = displayValue(v.max, v.unit, category, settings);
              const range =
                roundShown(lo.value) === roundShown(hi.value)
                  ? roundShown(lo.value)
                  : `${roundShown(lo.value)}–${roundShown(hi.value)}`;
              return `${NEO_FORECAST_FIELDS[v.field].label} ${range} ${lo.unit}`.trim();
            })
            .join(', ')}`,
        };
  const missing =
    snapshot.missing.length === 0
      ? null
      : `Not in this forecast: ${snapshot.missing.map((f) => NEO_FORECAST_FIELDS[f].label).join(', ')}.`;
  const provenance = `${snapshot.provider} · fetched ${dayLabel(snapshot.fetchedAt, zone)} ${clockLabel(snapshot.fetchedAt, zone)}`;
  const summary = [
    `Forecast: ${series.map((s) => s.label).join(' and ')}, ${unit}, ${dayLabel(from, zone)} ${clockLabel(from, zone)} to ${dayLabel(to, zone)} ${clockLabel(to, zone)}.`,
    ...snapshot.series.map((s, i) =>
      s.stats === null
        ? `${s.label}: no values.`
        : `${s.label}: ${roundShown(displayValue(s.stats.min, s.unit, forecastCategory(s.field), settings).value)} to ${roundShown(displayValue(s.stats.max, s.unit, forecastCategory(s.field), settings).value)} ${series[i]!.unit}.`
    ),
    calmest?.text === undefined ? '' : `${calmest.text}.`,
    missing ?? '',
    `${provenance}.`,
  ].filter((p) => p.length > 0);
  return {
    unit,
    series: series.map(({ unit: _u, ...s }) => s),
    from,
    to,
    min: values.length === 0 ? 0 : Math.min(...values),
    max: values.length === 0 ? 1 : Math.max(...values),
    calmest,
    missing,
    provenance,
    zone,
    summary: summary.join(' '),
  };
}

// ----- An allocation --------------------------------------------------------------

export interface AllocationSegmentView {
  label: string;
  role: 'use' | 'reserve' | 'other' | 'margin' | 'shortfall';
  /** Width in percent of the bar (the whole, plus any shortfall). */
  width: number;
  text: string;
  assumed: boolean;
}

export interface AllocationView {
  unit: string;
  whole: { label: string; text: string; assumed: boolean };
  segments: AllocationSegmentView[];
  /** Where the whole ends, in percent of the bar, when a shortfall runs past it. */
  wholeEnd: number;
  remainder: { kind: 'margin' | 'shortfall' | 'exact'; label: string; text: string };
  summary: string;
}

export function allocationView(
  snapshot: AllocationSnapshot,
  settings: UnitSettings
): AllocationView {
  const category = unitCategory(snapshot.unit);
  const text = (v: number) => {
    const d = displayValue(v, snapshot.unit, category, settings);
    return `${roundShown(d.value)} ${d.unit}`.trim();
  };
  const r = snapshot.remainder;
  const span = snapshot.whole.value + (r.kind === 'shortfall' ? r.value : 0);
  const pct = (v: number) => (span <= 0 ? 0 : (Math.max(0, v) / span) * 100);
  const segments: AllocationSegmentView[] = snapshot.segments.map((s) => ({
    label: s.label,
    role: s.role,
    width: pct(s.covered),
    text:
      s.covered < s.value - 1e-9
        ? `${text(s.covered).split(' ')[0]} / ${text(s.value)}`
        : text(s.value),
    assumed: s.origin === 'assumed',
  }));
  if (r.kind === 'margin') {
    segments.push({
      label: r.label,
      role: 'margin',
      width: pct(r.value),
      text: text(r.value),
      assumed: false,
    });
  } else if (r.kind === 'shortfall') {
    segments.push({
      label: r.label,
      role: 'shortfall',
      width: pct(r.value),
      text: text(r.value),
      assumed: false,
    });
  }
  const summary = [
    `${snapshot.whole.label}: ${text(snapshot.whole.value)}${snapshot.whole.origin === 'assumed' ? ' (assumed)' : ''}.`,
    ...snapshot.segments.map(
      (s) => `${s.label}: ${text(s.value)}${s.origin === 'assumed' ? ' (assumed)' : ''}.`
    ),
    r.kind === 'exact' ? 'Nothing left over.' : `${r.label}: ${text(r.value)}.`,
  ];
  return {
    unit: displayValue(0, snapshot.unit, category, settings).unit,
    whole: {
      label: snapshot.whole.label,
      text: text(snapshot.whole.value),
      assumed: snapshot.whole.origin === 'assumed',
    },
    segments,
    wholeEnd: pct(snapshot.whole.value),
    remainder: { kind: r.kind, label: r.label, text: text(r.value) },
    summary: summary.join(' '),
  };
}

// ----- Titles --------------------------------------------------------------------

/** The chart's title (the protocol's), and the line under it. */
export function answerChartHead(chart: NeoAnswerChart): { title: string; sub: string | null } {
  const title = neoChartTitle(chart);
  const snapshot = chart.snapshot;
  if (snapshot.kind === 'allocation') return { title, sub: null };
  const zone =
    snapshot.kind === 'bars' || snapshot.kind === 'forecast' ? snapshot.timezone : undefined;
  const { from, to } = chart.spec.window;
  const a = dayLabel(from, zone);
  const b = dayLabel(to, zone);
  return { title, sub: a === b ? a : `${a} – ${b}` };
}
